import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  listSimilarListings,
  scoreDemandQualification,
  type QualificationDto,
  type QualificationFinancing,
  type QualificationMotivation,
  type QualificationTimeline,
  type SimilarListingsDto,
} from '../lib/inquiry-ai-api.js';
import { V4Group, V4Pill, V4Row } from './ux-v4.js';

/**
 * Krok NAP4 bod A9 — dva panely v detailu poptávky:
 *
 *  - VÁŽNOST ZÁJEMCE (`scoreQualification`): makléř zaškrtne signály
 *    (financování, termín, motivace), server vrátí skóre S DŮVODY a zapíše
 *    je do historie poptávky. Je to POMOCNÝ ukazatel — nic nezamyká, žádná
 *    hodnota poptávku nezavírá (AI2 `qualification.ts`, „člověk ve smyčce").
 *  - PODOBNÉ NABÍDKY (`findSemanticMatches`): vedle filtru shod (CRM4) hledá
 *    podle VOLNÉHO TEXTU přání. Vysvětlení cituje jen slova, která jsou
 *    doopravdy v popisu nabídky; řazení podle významu je navíc a když AI
 *    neběží, panel to ŘEKNE a slovní shody zůstanou.
 */

const FINANCING: readonly QualificationFinancing[] = ['unknown', 'needed', 'confirmed'];
const TIMELINE: readonly QualificationTimeline[] = ['unknown', 'vague', 'concrete'];
const MOTIVATION: readonly QualificationMotivation[] = ['unknown', 'low', 'medium', 'high'];

export function DemandQualificationPanel({
  demandId,
  onScored,
}: {
  readonly demandId: string;
  /** Detail si po vyhodnocení natáhne historii aktivit (zápis důvodů). */
  readonly onScored?: () => void;
}): React.JSX.Element {
  const t = useT();
  const [financing, setFinancing] = React.useState<QualificationFinancing>('unknown');
  const [timeline, setTimeline] = React.useState<QualificationTimeline>('unknown');
  const [motivation, setMotivation] = React.useState<QualificationMotivation>('unknown');
  const [score, setScore] = React.useState<QualificationDto | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function evaluate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setScore(await scoreDemandQualification(demandId, { financing, timeline, motivation }));
      onScored?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-crm4-panel" data-testid="demand-qualification">
      <p className="rh-a2-meta">{t('webAdmin.demands.qualification.hint')}</p>
      <label>
        <span className="rh-a2-meta">{t('webAdmin.demands.qualification.financing')}</span>
        <select
          value={financing}
          data-testid="demand-qualification-financing"
          onChange={(e) => setFinancing(e.target.value as QualificationFinancing)}
        >
          {FINANCING.map((key) => (
            <option key={key} value={key}>
              {t(`webAdmin.demands.qualification.option.financing.${key}` as 'webAdmin.demands.qualification.option.financing.confirmed')}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="rh-a2-meta">{t('webAdmin.demands.qualification.timeline')}</span>
        <select
          value={timeline}
          data-testid="demand-qualification-timeline"
          onChange={(e) => setTimeline(e.target.value as QualificationTimeline)}
        >
          {TIMELINE.map((key) => (
            <option key={key} value={key}>
              {t(`webAdmin.demands.qualification.option.timeline.${key}` as 'webAdmin.demands.qualification.option.timeline.concrete')}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="rh-a2-meta">{t('webAdmin.demands.qualification.motivation')}</span>
        <select
          value={motivation}
          data-testid="demand-qualification-motivation"
          onChange={(e) => setMotivation(e.target.value as QualificationMotivation)}
        >
          {MOTIVATION.map((key) => (
            <option key={key} value={key}>
              {t(`webAdmin.demands.qualification.option.motivation.${key}` as 'webAdmin.demands.qualification.option.motivation.high')}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="rh-a2-btn rh-a2-btn--sm"
        disabled={busy}
        data-testid="demand-qualification-evaluate"
        onClick={() => void evaluate()}
      >
        {busy ? t('webAdmin.demands.qualification.working') : t('webAdmin.demands.qualification.evaluate')}
      </button>
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="demand-qualification-error">
          {error}
        </p>
      ) : null}
      {score !== null ? (
        <div data-testid="demand-qualification-result">
          <V4Pill tone={score.level === 'high' ? 'accent' : 'neutral'} testId="demand-qualification-level">
            {t('webAdmin.demands.qualification.result', {
              level: t(`webAdmin.demands.qualification.level.${score.level}` as 'webAdmin.demands.qualification.level.high'),
              score: score.total,
            })}
          </V4Pill>
          {/* DŮVODY posílá server (tatáž věta, která jde do historie) — UI si
              je neopisuje, aby se dvě pravdy nerozešly. */}
          {score.factors.length === 0 ? (
            <p className="rh-a2-meta" data-testid="demand-qualification-no-factors">
              {t('webAdmin.demands.qualification.noFactors')}
            </p>
          ) : (
            <ul data-testid="demand-qualification-reasons">
              {score.factors.map((factor) => (
                <li key={factor.name}>{factor.reason}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function SimilarListingsPanel({ demandId }: { readonly demandId: string }): React.JSX.Element {
  const t = useT();
  const [data, setData] = React.useState<SimilarListingsDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listSimilarListings(demandId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [demandId]);

  return (
    <div className="rh-crm4-panel" data-testid="demand-similar-listings">
      <p className="rh-a2-meta">{t('webAdmin.matches.similarHint')}</p>
      {loading ? (
        <p role="status" className="rh-a2-status">
          {t('webAdmin.matches.similarLoading')}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="demand-similar-listings-error">
          {t('webAdmin.matches.similarFailed')}
        </p>
      ) : null}
      {/* Proč řazení podle významu chybí (AI vypnutá) říká SERVER — makléř
          nedostane 500 ani prázdnou obrazovku bez vysvětlení. */}
      {data?.note != null ? (
        <p role="status" className="rh-a2-status" data-testid="demand-similar-listings-note">
          {data.note}
        </p>
      ) : null}
      {data !== null ? (
        <V4Group testId="demand-similar-listings-list" label={t('webAdmin.matches.similarHeading')}>
          {data.items.length === 0 ? (
            <V4Row title={t('webAdmin.matches.similarEmpty')} testId="demand-similar-listings-empty" />
          ) : null}
          {data.items.map((item) => (
            <V4Row
              key={item.propertyId}
              testId={`demand-similar-listing-${item.propertyId}`}
              title={item.explanation}
              chevron
              // Prostý odkaz, ne `<Link>`: panel žije v draweru, který se
              // kreslí a testuje bez kontextu routeru (vzor `MatchesPanel`).
              render={(className, content) => (
                <a href={`/nemovitosti/${item.propertyId}`} className={className} data-testid={`demand-similar-listing-${item.propertyId}`}>
                  {content}
                </a>
              )}
            />
          ))}
        </V4Group>
      ) : null}
    </div>
  );
}
